// series.ts — (TF-6b-2; planning 10-16 20:40 design §2, 22:00 answers) Admin → Series, the pages' rules, pure: traction
// in words ("Pending" until measured, never 0), a marked post's answer in words, every refusal in words, which actions an
// episode allows, the order buttons (off until the estimate is read; ingest has none yet), the owner's cap, and the list's
// fallback (the series opened in THIS browser, while the engine can't list them). Shapes: ingest's GET /series/:id and
// contracts' series and traction types, read defensively.

export type TractionRead = { state: "pending" } | { state: "ready"; views: number | null; followRate: number | null; shareRate: number | null };
export type TractionWindow = TractionRead & { hours: number; vsBaseline?: { follow: number | null; share: number | null } | null };
export type PostTractionView = TractionWindow[] | { state: "not_measured" | "unreadable"; reason: string };
export interface SeriesPostView {
  platform: string; platformRef?: string | null; permalink?: string | null; visibility?: string | null; visibilityReason?: string | null;
  postedAt?: string | null; traction?: PostTractionView;
}
export interface SeriesEpisodeView {
  seq: number; kind?: string; runId: string | null; status: string; postedAt?: string | null;
  planned?: { outcome?: string; stateAfter?: Record<string, number | string>; planVersion?: number } | null; posts: SeriesPostView[];
}
export interface SeriesView {
  id: string; patternId: string; title: string; premise: string; primaryPlatform: string; castRef: string | null; status: string;
  state?: { key: string; value: number | string; asOfSeq: number }[];
  plan?: { version: number; approvedVersion: number | null; approvedAt: string | null; approvedBy: string | null; endsSeries: boolean };
  episodes: SeriesEpisodeView[];
  /** (B117) the audience profile every order of the series carries (the backend's series_audiences); null: none.
   *  audienceError when it couldn't be read */
  audienceProfileId?: string | null; audienceError?: string;
  /** (B117-1) whether the chosen profile still gives the engine an audience: ok, missing, empty, broken */
  audienceState?: string;
  /** (B123) whether this growth pack's orders carry the audience (only the creator pack's do); absent: not known */
  audienceCarried?: boolean;
  /** (tf3-35c) each episode run's end, beside the series (the backend reads contracts' SeriesDetail, then the workers'
   *  summary); null with runsError when the summary couldn't be read */
  runs?: Record<string, RunEnd> | null; runsError?: string;
}
export type RunEnd = { status: string; endReason: string | null; dryRunStopped: boolean; /** (B119) stand-in clips */ rehearsal?: boolean };

/** Pure: (tf3-35c; zip 46 §3) what the page says about an ordered episode's run — a dry-run pilot stopped or cancelled, and
 *  how to order the real one (return it to planned, then order it again); null when there's nothing to say. */
export function episodeRunNote(ep: Pick<SeriesEpisodeView, "seq" | "status" | "runId">, series: Pick<SeriesView, "runs">): string | null {
  if (ep.status !== "ordered" || !ep.runId) return null;
  const r = series.runs?.[ep.runId];
  if (!r) return null;
  const what = ep.seq === 0 ? "pilot" : `episode ${ep.seq}`;
  // (B119) a rehearsal is never the made episode: said while it is open (a cancelled one reads as any cancelled run)
  if (r.rehearsal && (r.status === "running" || r.status === "pending"))
    return `This ${what} is a rehearsal with stand-in clips: for review only, never delivered or counted as made. Capture it if you need its record, then cancel it in the Run Inspector. To order the real ${what} after that, return it to planned and order it again.`;
  if (r.dryRunStopped) return `This ${what} is a dry run, stopped before clips. Capture it if you need its record, then cancel it in the Run Inspector. To order the real ${what} after that, return it to planned and order it again.`;
  if (r.status === "cancelled" && r.endReason === "dry_run_cancelled")
    return `This ${what} was a dry run and is cancelled: no clips were made. To order the real ${what}, return it to planned (with a reason), then order ${ep.seq === 0 ? "the pilot" : "the episodes"} again.`;
  if (r.status === "cancelled" || r.status === "failed")
    return `This ${what}'s run ${r.status === "failed" ? "failed" : "was cancelled"}. To order it again, return it to planned (with a reason), then order it.`;
  return null;
}

const pct = (r: number | null) => (r === null ? "—" : `${(r * 100).toFixed(1)}%`);
const times = (x: number | null | undefined) => (x == null ? null : `${x}× usual`);

/** Pure: one post's traction, as lines. Measured windows only show numbers; an empty read is "Pending", never 0. */
export function tractionLines(t: PostTractionView | undefined): string[] {
  if (!t) return ["Pending"];
  if (!Array.isArray(t)) return [t.state === "unreadable" ? `Couldn't be read: ${t.reason}` : `Not measured: ${t.reason}`];
  if (!t.length) return ["Pending"];
  return t.map((w) => w.state !== "ready" ? `${w.hours} h: Pending`
    : [`${w.hours} h: ${w.views ?? "—"} views`, `follows ${pct(w.followRate)}`, `shares ${pct(w.shareRate)}`,
      ...[times(w.vsBaseline?.follow) && `follows ${times(w.vsBaseline?.follow)}`, times(w.vsBaseline?.share) && `shares ${times(w.vsBaseline?.share)}`].filter(Boolean)]
      .join(" · "));
}

/** Pure: ingest's answer to "Mark as posted", in words. A post that isn't public counts for nothing. */
export function postedWords(r: { counted?: boolean; visibility?: string; reason?: string; seriesStatus?: string; measured?: boolean }): string {
  if (r.counted) return `Counts: public on the series' platform.${r.seriesStatus === "ended" ? " It was the plan's finale, so the series has ended." : ""}${r.measured ? "" : " Its numbers aren't read: connect the channel to read them."}`;
  if (r.visibility === "private") return `Not public, so it doesn't count${r.reason ? `: ${r.reason}` : ""}.`;
  if (r.visibility === "unknown") return `Couldn't tell whether it is public, so it doesn't count yet${r.reason ? `: ${r.reason}` : ""}. Mark it again to re-check.`;
  return `Recorded, not counted${r.reason ? `: ${r.reason}` : ""}.`;
}

/** Every refusal the series pages can meet, in words: contracts' SERIES_REFUSAL_CODES (the test checks each is here) and
 *  the backend's own. The engine's message follows it. */
export const SERIES_REFUSAL_WORDS: Record<string, string> = {
  GROWTH_TENANT_NOT_READY: "Growth's ingest key doesn't exist yet: growth-switch step 1 (an owner step).",
  PATTERN_NOT_FOUND: "That pattern isn't in the engine's catalogue.",
  PATTERN_NOT_ORDERABLE: "That pattern can't be ordered now (a draft past its waiver, or retired).",
  NOT_FOUND: "No such series or episode.",
  PILOT_ALREADY_ORDERED: "The pilot has been ordered (or posted) already.",
  PILOT_NOT_POSTED: "A series is picked up once its pilot is posted, public.",
  SERIES_NOT_ACTIVE: "The series isn't in a state that allows this; read it again.",
  BAD_LINK: "That link can't be read as a post on this platform.",
  POST_ALREADY_RECORDED: "That post is already recorded for another episode.",
  EPISODES_EXIST: "Some of those episodes exist already.",
  UNKNOWN_STATE_KEY: "That isn't one of the pattern's states.",
  VALIDATION_ERROR: "Something in the form isn't right.",
  NOT_MEASURED: "The channel didn't take the post.",
  PLAN_NOT_APPROVED: "The plan isn't approved: write it, then approve it.",
  PLAN_ALREADY_APPROVED: "This plan version is approved already.",
  NOTHING_TO_ORDER: "No planned episode of the approved plan is left to order.",
  PILOT_LOOK_UNREADABLE: "The pilot's look can't be read, so the episodes can't keep it.",
  EPISODE_NOT_RETURNABLE: "Only an ordered episode whose run failed or was cancelled goes back to planned.",
  RUN_NOT_ENDED: "Its run hasn't ended without a video.",
  RUN_UNREADABLE: "Its run can't be read right now; nothing was changed. Try again.",
  EPISODE_NOT_REPLANNABLE: "That episode can't be re-planned now.",
  NOT_ASKED: "Its run isn't asking for a re-plan.",
  AUDIT_UNAVAILABLE: "Not done; try again: the decision couldn't be recorded, so nothing changed.",
  NO_QUESTION_ID: "Its run's question has no id to answer by; read the run again.",
  QUESTION_CHANGED: "The question changed while you were looking; read it again.",
  ESTIMATE_UNAVAILABLE: "The engine couldn't make the estimate; nothing was ordered.",
  ESTIMATE_INVALID: "The engine's estimate came back off contract; nothing was ordered.",
  // (creative zip 43, planning 10-17 12:30) a series' person: 409 / 422 / 422
  PERSON_CHANGED: "This episode names a different person from its series; nothing was ordered. An episode keeps the series' person.",
  PERSON_CONFLICT: "The order names two different people; a series has one. Nothing was ordered.",
  PERSON_REQUIRED: "This series has its person but not their part (made before the engine recorded it), so the engine won't guess; nothing was ordered.",
  // the backend's own
  GROWTH_KEY_MISSING: "Growth's ingest key doesn't exist yet: growth-switch step 1 (an owner step), then VMS_GROWTH_INGEST_TOKEN.",
  LIST_NOT_AVAILABLE: "The engine can't list series yet.",
  ESTIMATE_NOT_AVAILABLE: "The engine can't estimate a series video yet.",
  ESTIMATE_UNREADABLE: "The engine's estimate couldn't be read; nothing was ordered.",
  CAP_BELOW_ESTIMATE: "The cap is below the estimate's high end; nothing was ordered.",
  PRESENTER_REQUIRED: "This pattern needs a person who speaks, and the series has none.",
  PRESENTER_UNAVAILABLE: "The series' person can't present (no speaking look).",
  VOICE_READ_FAILED: "The person's voice couldn't be read; try again.",
  ENGINE_UNREACHABLE: "Not done; try again: the engine couldn't be reached.",
  ENGINE_KEY_REFUSED: "The engine refused growth's ingest key.",
  UNAVAILABLE: "Not done; try again.",
};

/** Pure: an error from the series routes, in words: the code's words, then what the engine said. */
export function refusalText(e: unknown): string {
  const x = e as { code?: string; message?: string } | null;
  const words = x?.code ? SERIES_REFUSAL_WORDS[x.code] : undefined;
  const msg = x?.message?.trim();
  return words ? (msg && msg !== words ? `${words} (${msg})` : words) : msg || "not done; try again";
}

/** Pure: what an episode allows on this page. Re-planning goes through its run's "Needs you" card (TF-6b-1), never here. */
export function episodeActions(ep: SeriesEpisodeView, series: Pick<SeriesView, "status">): { markPosted: boolean; returnToPlanned: boolean; openRun: boolean } {
  const active = series.status === "pilot" || series.status === "picked_up";
  return {
    markPosted: ep.status !== "dropped",   // ingest refuses only a dropped episode (a pilot made the current way is marked too)
    returnToPlanned: active && ep.status === "ordered" && !!ep.runId,
    openRun: !!ep.runId,
  };
}

export type EstimateState = { state: "loading" } | { state: "ready"; lowCents: number; highCents: number; episodes: number | null;
    /** (B64) the batch's total (contracts' SeriesEpisodesEstimate.total); null for the pilot */
    total?: { lowUsdCents: number; highUsdCents: number } | null }
  | { state: "unavailable"; text: string };
export const money = (cents: number) => `$${(Math.max(0, cents) / 100).toFixed(2)}`;

/** Pure: an order button and its note. Off until the estimate is read; off with the engine's words while it can't be. */
export function orderButton(est: EstimateState, capCents: number | null): { enabled: boolean; note: string } {
  if (est.state === "loading") return { enabled: false, note: "Reading the estimate…" };
  if (est.state === "unavailable") return { enabled: false, note: est.text };
  const each = est.episodes ? ` each, ${est.episodes} episodes${est.total ? ` (all: ${money(est.total.lowUsdCents)}–${money(est.total.highUsdCents)})` : ""}` : "";
  const range = `Estimate ${money(est.lowCents)}–${money(est.highCents)}${each}.`;
  const bad = capError(capCents, est.highCents);
  return bad ? { enabled: false, note: `${range} ${bad}` } : { enabled: true, note: `${range} At most ${money(capCents!)}${est.episodes ? " each" : ""}.` };
}

/** Pure: the owner's cap (cents) against the estimate's high end; null = fine. */
export function capError(capCents: number | null, highCents: number): string | null {
  if (capCents === null || !Number.isInteger(capCents) || capCents <= 0) return "Enter a cap.";
  if (capCents < highCents) return `The cap is below the estimate's high end (${money(highCents)}).`;
  return null;
}
/** Pure: "$3.20" (or "3.2") → 320 cents; anything else → null. */
export function centsOf(text: string): number | null {
  const m = /^\s*\$?\s*(\d+)(?:\.(\d{1,2}))?\s*$/.exec(text);
  return m ? Number(m[1]) * 100 + Number((m[2] ?? "0").padEnd(2, "0")) : null;
}

/** The list's fallback: the series opened in THIS browser (newest first, at most 50). Storage can throw or be empty. */
export interface SeriesSeen { id: string; title: string; at: string }
export const HISTORY_KEY = "tf.admin.series.seen";
type Store = Pick<Storage, "getItem" | "setItem"> | null | undefined;
/** (TF-6b-3) `key`: the slate board keeps its own history the same way (SLATE_HISTORY_KEY). */
export function seriesHistory(store: Store, key = HISTORY_KEY): SeriesSeen[] {
  try {
    const v = JSON.parse(store?.getItem(key) ?? "[]");
    return Array.isArray(v) ? v.filter((x): x is SeriesSeen => typeof x?.id === "string" && typeof x?.title === "string") : [];
  } catch { return []; }   // catch-reason: unreadable storage is an empty history (the page says the list is this browser's)
}
export function rememberSeries(store: Store, s: { id: string; title: string }, now = new Date(), key = HISTORY_KEY): SeriesSeen[] {
  const next = [{ id: s.id, title: s.title, at: now.toISOString() }, ...seriesHistory(store, key).filter((x) => x.id !== s.id)].slice(0, 50);
  try { store?.setItem(key, JSON.stringify(next)); } catch { /* catch-reason: storage refused; the history just isn't kept */ }
  return next;
}

/** (B117; planning 10-25 19:10) admin-only words (not the customer words): beside the order buttons of a series with no
 *  audience. Since C2.16 the engine refuses a growth order that names nobody. */
export const NO_AUDIENCE_NOTE = "No audience: the engine will refuse this order. Choose one under \"The audience\" above.";
/** (B117) a profile as the audience select lists it */
export const audienceOptionLabel = (p: { id: string; label: string }) => `${p.label} (${p.id})`;
/** (B117-1; planning 10-25 19:55) beside the order buttons when the chosen profile has since become empty (the backend's
 *  AUDIENCE_EMPTY words) */
export const AUDIENCE_EMPTY_NOTE = "This audience has none of Pain points, Aspiration, Wants to see or Anti-patterns, so the engine would refuse its orders. Fill one on Admin → Audiences.";
/** (B117-1) the note an order button shows for the series' audience, or null */
/** (audit 10-27) the backend refuses these too (AUDIENCE_NOT_FOUND, AUDIENCE_CHAIN_BROKEN): said before the click */
export const AUDIENCE_MISSING_NOTE = "This audience is no longer a profile, so the order would be refused. Choose another under \"The audience\" above.";
export const AUDIENCE_BROKEN_NOTE = "This audience can't be read through its parents, so the order would be refused. Fix it on Admin → Audiences.";
/** (B123; planning 10-27 18:00) the pack in use sends no audience: the same problem is said, but the order isn't refused over it */
export const audienceNotSentNote = (why: string) => `The growth pack in use doesn't send an audience with its orders, so this order isn't refused: ${why} It matters only if the pack changes to creator.`;
const NOT_SENT_WHY: Record<string, string> = { none: "no audience is set.", empty: "this audience has none of Pain points, Aspiration, Wants to see or Anti-patterns.",
  missing: "this audience is no longer a profile.", broken: "this audience can't be read through its parents." };
export const audienceNoteOf = (s: { audienceProfileId?: string | null; audienceState?: string; audienceCarried?: boolean }): string | null => {
  const problem = !s.audienceProfileId ? "none" : ["empty", "missing", "broken"].includes(s.audienceState ?? "") ? s.audienceState! : null;
  if (!problem) return null;
  if (s.audienceCarried === false) return audienceNotSentNote(NOT_SENT_WHY[problem]!);
  return { none: NO_AUDIENCE_NOTE, empty: AUDIENCE_EMPTY_NOTE, missing: AUDIENCE_MISSING_NOTE, broken: AUDIENCE_BROKEN_NOTE }[problem]!;
};
